import { FileText, Phone, TrendingUp, MapPin, Users, DollarSign } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const CourtOrderModification = () => {
  return (
    <GenericPracticePage
      title="Court Order Modification Attorney"
      subtitle="Modifying existing custody, visitation, and child support orders when circumstances change. Protecting your family's best interests as life evolves."
      icon={<FileText className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Texas Court Order Modification Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Life doesn't stand still after divorce or custody orders. Jobs change, people relocate, children's needs evolve, and income fluctuates. When circumstances substantially change, existing court orders may no longer work or be fair. Texas law allows modification of custody, visitation, and support orders when appropriate.
            </p>
            <p className="text-lg text-muted-foreground">
              We help parents navigate the modification process to ensure court orders reflect current realities while protecting children's best interests.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Types of Modifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Users className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Custody Modification</h3>
                <p className="text-sm text-muted-foreground">
                  Changing who has primary custody or joint managing conservatorship designation based on substantial change in circumstances.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <MapPin className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Visitation Modification</h3>
                <p className="text-sm text-muted-foreground">
                  Adjusting possession and access schedules when current arrangements no longer work for the family.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <DollarSign className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Child Support Modification</h3>
                <p className="text-sm text-muted-foreground">
                  Increasing or decreasing child support based on changed financial circumstances or needs.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Modifying Child Custody Orders</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold mb-3">Legal Standard for Modification</h3>
              <p className="text-muted-foreground mb-4">
                To modify custody in Texas, you must prove:
              </p>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <span className="text-accent font-bold text-lg">1.</span>
                  <div>
                    <p className="font-bold">Material and Substantial Change in Circumstances</p>
                    <p className="text-sm text-muted-foreground">Significant changes since the last order affecting the child's wellbeing</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-accent font-bold text-lg">2.</span>
                  <div>
                    <p className="font-bold">Modification Is in Child's Best Interest</p>
                    <p className="text-sm text-muted-foreground">The proposed change would benefit the child</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-bold mb-3">Common Grounds for Custody Modification:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Parent relocating to another city/state</li>
                  <li>• Change in child's needs as they age</li>
                  <li>• Parent's job schedule changed significantly</li>
                  <li>• Primary parent preventing visitation</li>
                  <li>• Evidence of neglect or endangerment</li>
                  <li>• Child's preference (if 12 or older)</li>
                  <li>• Parent remarried/new household situation</li>
                  <li>• Parent's substance abuse or mental health issues</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-bold mb-3">One-Year Restriction (Important!):</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You generally cannot file to modify custody within one year of the previous order, UNLESS:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Child's present environment endangers their wellbeing</li>
                  <li>• Primary parent voluntarily relinquished custody</li>
                  <li>• Primary parent consents to modification</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Modifying Child Support Orders</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold mb-3">When Can Support Be Modified?</h3>
              <p className="text-muted-foreground mb-4">
                Child support can be modified if:
              </p>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  <strong>1. Material and Substantial Change:</strong> Significant change in circumstances affecting ability to pay or child's needs
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>2. Three-Year Rule:</strong> It's been 3+ years since the last order AND the current order differs from guidelines by 20% or $100/month
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md">
                <TrendingUp className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-lg font-bold mb-3">Reasons to Increase Support:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Paying parent received significant raise/promotion</li>
                  <li>• Child's needs increased (medical, educational)</li>
                  <li>• Paying parent inherited money or assets</li>
                  <li>• Cost of childcare increased substantially</li>
                  <li>• Medical insurance costs increased</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md">
                <TrendingUp className="h-8 w-8 text-destructive mb-3" />
                <h3 className="text-lg font-bold mb-3">Reasons to Decrease Support:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Paying parent lost job or income reduced</li>
                  <li>• Paying parent became disabled</li>
                  <li>• Paying parent has additional children to support</li>
                  <li>• Child's needs decreased</li>
                  <li>• Paying parent now provides health insurance</li>
                </ul>
              </div>
            </div>

            <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg mt-6">
              <p className="font-bold mb-2">Important: Support obligation doesn't automatically change!</p>
              <p className="text-sm text-muted-foreground">
                You must file for modification with the court. Simply stopping payments or paying less can result in contempt charges and enforcement actions. Keep paying the court-ordered amount until a judge modifies it.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Modifying Visitation/Possession Orders</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                Visitation schedules often need adjustment as children grow, parents' work schedules change, or families relocate. Courts can modify possession orders to better fit current needs.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-bold mb-2">Common Reasons for Modification:</h3>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Parent's work schedule changed</li>
                    <li>• Child's school/activity schedule conflicts</li>
                    <li>• Geographic distance increased/decreased</li>
                    <li>• Child's preference (if 12+)</li>
                    <li>• Need for more flexible schedule</li>
                    <li>• Expansion of visitation when appropriate</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Courts Consider:</h3>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Child's best interest</li>
                    <li>• Quality of parent-child relationship</li>
                    <li>• Practical feasibility of schedule</li>
                    <li>• Child's age and needs</li>
                    <li>• Parents' ability to cooperate</li>
                    <li>• Child's school and activities</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Relocation and Modification</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <MapPin className="h-10 w-10 text-accent mb-4" />
              <p className="text-muted-foreground mb-4">
                When a parent wants to relocate with the child to another city or state, this often requires custody/visitation modification.
              </p>
              <p className="font-bold mb-2">Primary parent wanting to relocate must show:</p>
              <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                <li>• Relocation is in the child's best interest</li>
                <li>• Reasonable purpose for the move (job, family support, remarriage)</li>
                <li>• Plan for maintaining other parent's relationship with child</li>
              </ul>
              <p className="text-muted-foreground">
                <strong>Non-primary parent can object</strong> and argue relocation isn't in child's best interest. Court weighs benefits of move against harm to parent-child relationship.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">The Modification Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Petition to Modify</h3>
                  <p className="text-sm text-muted-foreground">File petition explaining what you want changed and why (material/substantial change in circumstances).</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Service on Other Parent</h3>
                  <p className="text-sm text-muted-foreground">Other parent must be properly served with the petition and has time to respond.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Temporary Orders (if needed)</h3>
                  <p className="text-sm text-muted-foreground">Court can set temporary arrangements while the case is pending.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">Mediation</h3>
                  <p className="text-sm text-muted-foreground">Courts typically require mediation to try to reach agreement before trial.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">5</div>
                <div>
                  <h3 className="font-bold mb-1">Trial (if no agreement)</h3>
                  <p className="text-sm text-muted-foreground">Present evidence proving material change and that modification serves child's best interest.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">6</div>
                <div>
                  <h3 className="font-bold mb-1">Modified Order</h3>
                  <p className="text-sm text-muted-foreground">Judge issues new order modifying or denying modification. New order supersedes old one.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Need to Modify a Court Order?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us today to discuss modifying custody, visitation, or child support orders. We'll evaluate your case and fight for fair modifications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default CourtOrderModification;
